import { Injectable } from '@angular/core'
import posthog from 'posthog-js'
import { catchError, from, map, Observable, of, tap } from 'rxjs'
import { ParseService } from 'src/app/parse.service'
import { PosthogService } from './posthog.service'

type FeatureFlag =
    | 'promo-code'
    | 'signup-login-v2'
    | 'templates-page-v2'
    | 'onboarding-v2'
    | 'developer-console'
    | 'mailing-list-upsell-v2'
    | 'mobile-filters-non-ios-v2'
    | 'subscription-v2-page'
    | 'upload-my-own-design-v2'
    | 'banner-templates-page'
@Injectable({
    providedIn: 'root',
})
export class FeatureFlagService {
    constructor(
        private posthogService: PosthogService,
        private parseService: ParseService
    ) {}

    private isFeatureFlagEnabled(flag: FeatureFlag): Observable<boolean> {
        return from(this.posthogService.isFeatureEnabled(flag)).pipe(
            tap((isEnabled) => {
                if (isEnabled) {
                    posthog.capture('$feature_flag_called', { feature_flag: flag })
                }
            }),
            catchError((err) => {
                posthog.capture('$feature_error', {
                    feature_flag: flag,
                    error_message: err.message,
                    error_stack: err.stack,
                })
                this.parseService.reportUIError(err, 'FeatureFlagService.isFeatureFlagEnabled', '')
                return of(false)
            })
        )
    }

    public getFeatureFlagVariant(flag: FeatureFlag): Observable<string | null> {
        const defaultVariant = 'control'

        return from(this.posthogService.getFeatureFlagVariant(flag)).pipe(
            map((variant) => variant || defaultVariant),
            tap((variant) => {
                if (variant) {
                    posthog.capture('$feature_flag_called', { feature_flag: flag, variant })
                }
            }),
            catchError((err) => {
                posthog.capture('$feature_error', {
                    feature_flag: flag,
                    error_message: err.message,
                    error_stack: err.stack,
                })
                this.parseService.reportUIError(err, 'FeatureFlagService.isMultiVariantFeatureFlagEnabled', '')
                return of(defaultVariant)
            })
        )
    }

    isPromoCodeEnabled(): Observable<boolean> {
        return this.isFeatureFlagEnabled('promo-code')
    }

    isTemplatePageV1Enabled(): Observable<boolean> {
        return this.getFeatureFlagVariant('templates-page-v2').pipe(map((variant) => variant === 'control'))
    }
    isTemplatePageV2Enabled(): Observable<boolean> {
        return this.getFeatureFlagVariant('templates-page-v2').pipe(map((variant) => variant === 'templates-page-v2'))
    }

    isLoginControlVariant(): Observable<boolean> {
        return this.getFeatureFlagVariant('signup-login-v2').pipe(map((variant) => variant === 'control'))
    }
    isLoginVariantTwo(): Observable<boolean> {
        return this.getFeatureFlagVariant('signup-login-v2').pipe(map((variant) => variant === 'signup-login-v2'))
    }

    isOnboardingControlVariant(): Observable<boolean> {
        return this.getFeatureFlagVariant('onboarding-v2').pipe(map((variant) => variant === 'control'))
    }

    isOnboardingVariantTwo(): Observable<boolean> {
        return this.getFeatureFlagVariant('onboarding-v2').pipe(map((variant) => variant === 'onboarding-v2'))
    }

    isDeveloperConsoleEnabled(): Observable<boolean> {
        return this.isFeatureFlagEnabled('developer-console')
    }

    isMobileFiltersNonIosV2Enabled(): Observable<boolean> {
        return this.isFeatureFlagEnabled('mobile-filters-non-ios-v2')
    }

    isSubscriptionV2ControlVariant(): Observable<boolean> {
        return this.getFeatureFlagVariant('subscription-v2-page').pipe(map((variant) => variant === 'control'))
    }

    isSubscriptionV2VariantTwo(): Observable<boolean> {
        return this.getFeatureFlagVariant('subscription-v2-page').pipe(map((variant) => variant === 'subscription-v2-page'))
    }

    isUploadMyOwnDesignV2ControlVariant(): Observable<boolean> {
        return this.getFeatureFlagVariant('upload-my-own-design-v2').pipe(map((variant) => variant === 'control'))
    }

    isUploadMyOwnDesignV2VariantTwo(): Observable<boolean> {
        return this.getFeatureFlagVariant('upload-my-own-design-v2').pipe(map((variant) => variant === 'upload-my-own-design-v2'))
    }

    isTemplatesPageBannerEnabled(): Observable<boolean> {
        return this.getFeatureFlagVariant('banner-templates-page').pipe(map((variant) => variant === 'banner-templates-page'))
    }
}
